// @fileoverview : 영업관리 > 납품현황
import { users } from "@/api/users/useUsersQuery";
import { itemTypeColor } from "@/constants/items";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { LotDetailForm } from "@/features/lot/form/LotDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/DetailForm";
import { PurchaseOrderItemsDetailForm } from "@/features/purchaseOrderItems/form/PurchaseOrderItemDetailForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import { StockPlanDetailForm } from "@/features/stockPlan/form/DetailForm";
import { TransportationDetailForm } from "@/features/transportation/form/TransportationDetailForm";
import DatetimeUtil from "@/utils/dateTimeUtil";
import { Avatar, Badge, Button, Flex, Text, Tooltip } from "@mantine/core";
import {
  WmsLogsGet200ResponseRowsInnerActionTypeEnum,
  WmsLogWithPartnerGet200Response,
  WmsLogWithPartnerGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconFilterPlus, IconRecycle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Column, FormatterProps } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const DeliveryStatusPage = () => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const { query, setQuery, resetQueryStrings, setParams } = useStandardLayout();
  // const [isBeta] = useLocalStorage<boolean>({
  //   key: "isBeta",
  //   defaultValue: false, // 기본값은 모든 컬럼이 체크됨
  // });
  const { data: userInfo } = useQuery(users.who({}));

  useEffect(() => {
    if (query.length === 0) {
      // query가 비어 있을 때만 실행
      setQuery([
        { purchaseOrderItemId: { $not: null } },
        {
          actionType: {
            $or: [
              { $eq: WmsLogsGet200ResponseRowsInnerActionTypeEnum.OUTGOING },
              { $eq: WmsLogsGet200ResponseRowsInnerActionTypeEnum.INCOMING },
            ],
          },
        },
      ]);
    }
  }, [query]);

  useEffect(() => {
    const updatedParams = {
      partnerCode: userInfo?.data?.partner?.code,
    };
    setParams((prev) => ({
      ...prev,
      ...updatedParams,
    }));
  }, [userInfo]);

  const columns: readonly Column<WmsLogWithPartnerGet200ResponseRowsInner>[] = [
    {
      key: "purchaseOrderItemId",
      name: t("purchaseOrder.id"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <PurchaseOrderItemsDetailForm
                  purchaseOrderItemId={formatterProps?.row?.purchaseOrderItemId ?? 0}
                  pageKey={
                    formatterProps?.row?.purchaseOrderItem?.direction === "INBOUND"
                      ? "purchaseOrderItems"
                      : "orderItems"
                  }
                />,
                null,
                "",
                false
              );
            }}
          >
            {formatterProps?.row?.purchaseOrderItemId}
          </LinkAnchor>
        );
      },
    },
    {
      key: "partnerName",
      name: t("partner.name"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <PartnerDetailForm partnerCode={formatterProps?.row?.partnerCode} />,
                null,
                ""
              );
            }}
          >
            {formatterProps?.row?.partnerName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "transportation.code",
      name: t("transportation.code"),
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <TransportationDetailForm
                  transportationID={formatterProps?.row?.transportation?.id}
                />,
                null,
                ""
              );
            }}
          >
            {formatterProps?.row?.transportation?.code ?? ""}
          </LinkAnchor>
        );
      },
    },
    {
      key: "vehicleCode",
      name: t("vehicle.code"),
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <TransportationDetailForm
                  transportationID={formatterProps?.row?.transportation?.id}
                />,
                null,
                ""
              );
            }}
          >
            {formatterProps?.row?.transportation?.vehicleCode ?? ""}
          </LinkAnchor>
        );
      },
    },
    {
      key: "itemType",
      name: t("item.type"),
      sortable: true,
      resizable: true,
      formatter: (
        formatterProps: FormatterProps<WmsLogWithPartnerGet200ResponseRowsInner, unknown>
      ) => {
        return (
          <Badge
            size="lg"
            color={itemTypeColor[formatterProps?.row?.itemType as keyof typeof itemTypeColor]}
          >
            {t(formatterProps?.row?.itemType as string)}
          </Badge>
        );
      },
    },
    {
      key: "itemName",
      name: t("item.name"),
      sortable: true,
      resizable: true,
      formatter: (
        formatterProps: FormatterProps<WmsLogWithPartnerGet200ResponseRowsInner, unknown>
      ) => {
        return (
          <Flex h="100%" align="center">
            <Text fz="0.875rem">{formatterProps.row.itemName}</Text>
          </Flex>
        );
      },
    },
    {
      key: "gradeName",
      name: t("item.gradeName"),
      sortable: true,
      resizable: true,
      formatter: (
        formatterProps: FormatterProps<WmsLogWithPartnerGet200ResponseRowsInner, unknown>
      ) => {
        return (
          <Flex h="100%" align="center">
            <Text fz="0.875rem">{formatterProps.row.gradeName}</Text>
          </Flex>
        );
      },
    },
    {
      key: "itemCategory",
      name: t("item.category"),
      sortable: true,
      resizable: true,
      formatter: (
        formatterProps: FormatterProps<WmsLogWithPartnerGet200ResponseRowsInner, unknown>
      ) => {
        return (
          <Flex h="100%" align="center">
            <Text fz="0.875rem">{formatterProps.row.itemCategoryName}</Text>
          </Flex>
        );
      },
    },
    {
      key: "abstractItemCode",
      name: t("item.abstractItemCode"),
      sortable: true,
      resizable: true,
      formatter: (
        formatterProps: FormatterProps<WmsLogWithPartnerGet200ResponseRowsInner, unknown>
      ) => {
        return (
          <Flex h="100%" align="center">
            <Text fz="0.875rem">{formatterProps.row.abstractItemCode}</Text>
          </Flex>
        );
      },
    },

    {
      key: "lotInfo.name",
      name: t("lot.name"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <LotDetailForm
                  lotName={formatterProps?.row?.actionParam?.params?.lotInfo?.name}
                  lotId={formatterProps?.row?.actionParam?.params?.lotId}
                />,
                null,
                ""
              );
            }}
          >
            {formatterProps?.row?.actionParam?.params?.lotInfo?.name}
          </LinkAnchor>
        );
      },
    },
    {
      key: "quantity",
      name: t("inventory.quantity"),
      sortable: true,
      resizable: true,

      formatter: (formatterProps) => {
        return (
          <Flex justify={"end"} align={"center"} h="100%">
            <Text ta="end">{formatterProps?.row?.actionParam?.params?.quantity}</Text>
          </Flex>
        );
      },
    },
    {
      key: "deliveryDeadline",
      name: t("delivery.deadline"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex justify={"flex-start"} align={"center"} h="100%">
            <Text>
              {formatterProps?.row?.purchaseOrderItem?.deliveryDeadline
                ? DatetimeUtil(formatterProps?.row?.purchaseOrderItem?.deliveryDeadline as string)
                : ""}
            </Text>
          </Flex>
        );
      },
    },
  ];

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="flex-end" rowGap="md">
        <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
          {t("common.reset")}
        </Button>
      </Flex>
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="xl" fw={600}>
            {t("common.list", { item: `${t("delivery.deliveryStatus")}` })}
          </Text>
          <Text fz="xs">{t("common.subTitle", { item: `${t("delivery.deliveryStatus")}` })}</Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip
              label={
                '사용자 정의 필터입니다. "필터 추가" 또는 "정렬 추가" 버튼을 클릭하여 조건을 입력합니다.'
              }
            >
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<WmsLogWithPartnerGet200Response, WmsLogWithPartnerGet200ResponseRowsInner>
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<WmsLogWithPartnerGet200Response> />
      </Flex>
    </Flex>
  );
};
